import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { PageHeader } from '../../app/PageHeader'
import copyJson from '../../demo/fixtures/copy.json'
import { useCreativeLibrary, useCreativeStats } from '../../demo/selectors'
import { demoProducts, useDemoStore } from '../../demo/store'
import type { CreativeLibraryItem } from '../../demo/types'
import { Badge, Button, EmptyState, Icon, Modal, Toast, ToastHost } from '../../ui'
import { AiGenerateDialog } from './AiGenerateDialog'
import { CreativeGrid } from './CreativeGrid'
import { CreativeLightbox } from './CreativeLightbox'
import { FilterRail } from './FilterRail'
import { creativeCopy, uploadCreativeFile } from './copy'
import styles from './CreativesPage.module.css'
import { useCreativeFilter } from './useCreativeFilter'

export function CreativesPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filterState = useCreativeFilter()
  const library = useCreativeLibrary(filterState.filter)
  const emptyFilter = useMemo(() => ({}), [])
  const allLibrary = useCreativeLibrary(emptyFilter)
  const stats = useCreativeStats()
  const campaigns = useDemoStore((state) => state.campaigns)
  const attachCreatives = useDemoStore((state) => state.attachCreatives)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [aiOpen, setAiOpen] = useState(false)
  const [targetCreative, setTargetCreative] = useState<CreativeLibraryItem | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [suggestOpen, setSuggestOpen] = useState(false)
  const pointerRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const preview = allLibrary.find((creative) => creative.id === previewId) ?? null
  const initialProductId = filterState.productId ?? demoProducts[0]?.id ?? ''

  useEffect(() => {
    if (params.get('gen') !== '1') return
    const timer = window.setTimeout(() => {
      setAiOpen(true)
      setParams((current) => { const next = new URLSearchParams(current); next.delete('gen'); return next }, { replace: true })
    }, 0)
    return () => window.clearTimeout(timer)
  }, [params, setParams])

  const selectTarget = (campaignId: string, creative: CreativeLibraryItem) => {
    attachCreatives(campaignId, [creative.id])
    const campaignName = campaigns.find((campaign) => campaign.id === campaignId)?.adName ?? '广告任务'
    setTargetCreative(null)
    setPreviewId(null)
    void navigate(`/monitor/${campaignId}?focus=creatives`, { state: { creativeToast: `已加入「${campaignName}」的投放素材，勾选后即可投放` } })
  }
  const useForAd = (creative: CreativeLibraryItem) => {
    const targets = campaigns.filter((campaign) => campaign.productId === creative.productId)
    if (!targets.length) { setToast('这个商品还没有广告任务，先去创建广告'); return }
    if (targets.length === 1) selectTarget(targets[0]!.id, creative)
    else setTargetCreative(creative)
  }
  const upload = async (file?: File) => {
    if (!file) return
    const result = await uploadCreativeFile(file, initialProductId)
    setToast(result.error ?? '已上传并入库')
  }
  const currentUsed = library.filter((creative) => creative.usedByAds > 0).length
  const suggestions = useMemo(() => {
    const byProduct = new Map<string, number>()
    const kols = new Set<string>()
    for (const item of allLibrary) {
      byProduct.set(item.productId, (byProduct.get(item.productId) ?? 0) + 1)
      if (item.kol) kols.add(item.kol)
    }
    const products = [...byProduct.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3)
      .map(([id]) => demoProducts.find((product) => product.id === id)?.name).filter(Boolean) as string[]
    return [...products, ...[...kols].slice(0, 3)]
  }, [allLibrary])

  return (
    <>
      <PageHeader data-scope="creatives-header" title={copyJson.pages.creatives.title} subtitle={copyJson.pages.creatives.subtitle} action={<div className={styles.headerActions}><Button variant="secondary" onClick={() => inputRef.current?.click()}><Icon name="upload" size={18} />{creativeCopy.upload}</Button><Button onClick={() => setAiOpen(true)}><Icon name="sparkle" size={18} />{creativeCopy.generate}</Button></div>} />
      <input ref={inputRef} type="file" hidden accept="image/*,video/*" onChange={(event) => { void upload(event.target.files?.[0]); event.currentTarget.value = '' }} />
      <div className={styles.layout} data-scope="creatives-layout">
        <main>
          <div className={styles.toolbar} data-scope="creatives-toolbar">
            <span className={styles.count}>共 {library.length} 条素材 · {currentUsed} 条使用中</span>
            <div className={styles.searchWrap} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setSuggestOpen(false) }}>
              <label className={styles.search} onMouseDown={() => { pointerRef.current = true }}>
                <Icon name="search" size={17} /><span className={styles.srOnly}>{creativeCopy.searchLabel}</span>
                <input value={filterState.q} placeholder={creativeCopy.searchPlaceholder} role="combobox" aria-expanded={suggestOpen} aria-controls="creative-search-suggest"
                  onFocus={() => { if (!pointerRef.current) setSuggestOpen(true) }}
                  onClick={() => { setSuggestOpen((open) => !open); pointerRef.current = false }}
                  onKeyDown={(event) => { if (event.key === 'Escape') setSuggestOpen(false) }}
                  onChange={(event) => { filterState.setQ(event.target.value); setSuggestOpen(false) }} />
              </label>
              {suggestOpen ? <div id="creative-search-suggest" className={styles.suggest}>
                <p className={styles.suggestHint}>{creativeCopy.suggestHint}</p>
                <div className={styles.chips}>{suggestions.length ? suggestions.map((word) => <button key={word} type="button" className={styles.chip} onClick={() => { filterState.setQ(word); setSuggestOpen(false) }}>{word}</button>) : <span className={styles.suggestHint}>{creativeCopy.suggestEmpty}</span>}</div>
              </div> : null}
            </div>
            {filterState.hasActive ? <button className={styles.reset} type="button" onClick={filterState.reset}>{creativeCopy.reset}</button> : null}
          </div>
          {library.length ? <CreativeGrid creatives={library} onPreview={(creative) => setPreviewId(creative.id)} onUse={useForAd} /> : <div className={styles.emptyWrap}>{stats.total === 0 ? <div><EmptyState icon="vault" title={creativeCopy.empty} text={creativeCopy.emptyText} /><div className={styles.emptyAction}><Button onClick={() => inputRef.current?.click()}>{creativeCopy.upload}</Button></div></div> : <EmptyState title={creativeCopy.noMatches} text={creativeCopy.noMatchesText} />}</div>}
        </main>
        <FilterRail creatives={allLibrary} filter={filterState.filter} source={filterState.source} kind={filterState.kind} usage={filterState.usage} productId={filterState.productId} onSource={filterState.setSource} onKind={filterState.setKind} onUsage={filterState.setUsage} onProduct={filterState.setProductId} />
      </div>
      <p className={styles.footnote} data-scope="creatives-footnote"><Icon name="info" size={16} />{creativeCopy.footnote}</p>
      {preview ? <CreativeLightbox key={preview.id} creative={preview} onClose={() => setPreviewId(null)} onUse={useForAd} onToast={setToast} /> : null}
      {aiOpen ? <AiGenerateDialog open initialProductId={initialProductId} onClose={() => setAiOpen(false)} onToast={setToast} /> : null}
      <Modal open={Boolean(targetCreative)} title="选择广告任务" hint="这件商品有多个广告任务，请选择素材要用于哪一条" onClose={() => setTargetCreative(null)}>
        <div>{targetCreative ? campaigns.filter((campaign) => campaign.productId === targetCreative.productId).map((campaign) => <button key={campaign.id} type="button" className={styles.targetOption} onClick={() => selectTarget(campaign.id, targetCreative)}><span>{campaign.adName}</span><Badge tone={campaign.status === 'running' ? 'green' : campaign.status === 'attention' ? 'red' : campaign.status === 'paused' ? 'orange' : campaign.status === 'pending' ? 'blue' : 'gray'}>{campaign.status === 'running' ? '投放中' : campaign.status === 'attention' ? '需注意' : campaign.status === 'paused' ? '已暂停' : campaign.status === 'pending' ? '待确认' : '已完成'}</Badge><Icon name="chevron-right" size={16} /></button>) : null}</div>
      </Modal>
      {toast ? <ToastHost><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </>
  )
}
